import React, { useState } from "react"

import { gql, useQuery } from "hooks/urql"

import AddUser from "components/LegacyMessageThreads/Details/AddUser"
import AvatarGroup from "components/LegacyMessageThreads/Details/AvatarGroup"
import LocalTime from "components/LocalTime"
import Avatar from "components/Messaging/Avatar"
import NotificationContainer from "components/NotificationContainer"
import Pagination from "components/Pagination"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import HideThreadButton from "./HideThreadButton"
import Message from "./Message"

export type MessageThreadProps = {
  id: number
}

const MessageThread: React.FC<MessageThreadProps> = ({ id }) => {
  const [page, setPage] = useState(1)
  const [result] = useQuery({
    query: messageThreadsQuery,
    variables: { id, page },
  })

  return (
    <>
      <NotificationContainer />
      <UrqlLoadingIndicator result={result}>
        {({ data: { messageThread } }) => (
          <>
            {messageThread.hidden && (
              <div className="alert alert-info mb-2 p-3">
                This thread is hidden from all participants.
              </div>
            )}
            <HideThreadButton
              messageThreadId={messageThread.id}
              hidden={messageThread.hidden}
            />

            <div className="mb-4 flex">
              <AvatarGroup
                messageThreadId={messageThread.id}
                users={messageThread.users}
                canDelete
                includeLinks
              />
              <AddUser messageThreadId={messageThread.id} viewerType="Admin" />
            </div>

            {messageThread.messages.data.map(message => (
              <Message key={message.id} message={message} />
            ))}
            {messageThread.messages.currentPage >=
              messageThread.messages.totalPages && (
              <div className="mb-4 flex items-center gap-2.5">
                <a
                  href={messageThread.author.showPath}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Avatar
                    src={messageThread.author.avatar.thumbUrl}
                    alt={messageThread.author.messagingDisplayName}
                  />
                </a>
                <div className="italic">
                  Thread created by {messageThread.author.messagingDisplayName}
                  {" on "}
                  <LocalTime timestamp={messageThread.createdAt} />
                </div>
              </div>
            )}
            <Pagination
              currentPage={messageThread.messages.currentPage}
              totalPages={messageThread.messages.totalPages}
              changePage={setPage}
            />
          </>
        )}
      </UrqlLoadingIndicator>
    </>
  )
}

const messageThreadsQuery = gql`
  query MessageThreads($id: ID!, $page: Int!) {
    messageThread(id: $id) {
      id
      title
      hidden
      showPath
      messagesCount: unfilteredMessagesCount
      createdAt
      author {
        id
        messagingDisplayName
        showPath
        avatar {
          thumbUrl
        }
      }
      users {
        id
        type
        messagingDisplayName
        showPath
        avatar {
          thumbUrl
        }
      }
      messages: unfilteredMessages(page: $page) {
        currentPage
        totalPages
        data {
          id
          hidden
          content
          createdAt
          author {
            id
            messagingDisplayName
            showPath
            avatar {
              thumbUrl
            }
          }
          attachment {
            filename
            downloadUrl
            previewUrl
          }
          readRecipients {
            id
            messagingDisplayName
          }
        }
      }
    }
  }
`

export default MessageThread
